import { FormEvent, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../api";
import { ChatPicker } from "../components/ChatPicker";
import { Badge, Button, Empty, Field, Input, Row, Rows, Section } from "../components/ui";

interface Tenant {
  id: number;
  slug: string;
  name: string;
  timezone: string;
  status: string;
}
interface DataTruckStatus {
  configured: boolean;
  base_url: string | null;
  username: string | null;
}
interface DataTruckSecret {
  base_url: string;
  username: string;
  password: string;
}
interface AdminRow {
  id: number;
  telegram_id: string;
  display_name: string | null;
}
interface MappingRow {
  id: number;
  mc_id: number;
  telegram_chat_id: string;
  mc_name_snapshot: string | null;
}
interface FleetChatStatus {
  telegram_chat_id: string | null;
}
interface ReportSettings {
  configured: boolean;
  hr_target_drivers: number | null;
  hr_target_lease: number | null;
  hr_target_company_driver: number | null;
  hr_target_company_owner: number | null;
}
interface ScheduleRow {
  id: number;
  kind: string;
  cron_expression: string;
  timezone: string;
  is_active: boolean;
}
interface RunRow {
  id: number;
  kind: string;
  trigger: string;
  status: string;
  started_at: string;
  total_deliveries: number;
  successful_deliveries: number;
  error: string | null;
}
interface RunNowResponse {
  kind: string;
  total_chunks: number;
  preview: string;
}

const KINDS = ["admin_totals", "company", "fleet_statuses"] as const;
const HR_FIELDS = [
  { key: "hr_target_drivers", label: "Drivers" },
  { key: "hr_target_lease", label: "Lease" },
  { key: "hr_target_company_driver", label: "Company" },
  { key: "hr_target_company_owner", label: "Owner" },
] as const;

function statusTone(status: string) {
  if (status === "succeeded") return "good" as const;
  if (status === "failed") return "bad" as const;
  if (status === "partial") return "warn" as const;
  return "neutral" as const;
}

export default function CustomerDetail() {
  const { id } = useParams();
  const tenantId = Number(id);
  const base = `/api/admin/tenants/${tenantId}`;
  const qc = useQueryClient();
  const refresh = (key: string) => qc.invalidateQueries({ queryKey: [key, tenantId] });

  const tenant = useQuery<Tenant>({
    queryKey: ["tenant", tenantId],
    queryFn: () => api.get<Tenant>(base),
  });

  return (
    <div className="min-h-screen bg-canvas p-6">
      <div className="mx-auto max-w-4xl space-y-6">
        <header className="flex items-center justify-between">
          <div>
            <Link to="/" className="text-sm text-primary">
              ← Customers
            </Link>
            <h1 className="mt-1 text-2xl font-semibold">{tenant.data?.name ?? "…"}</h1>
            <div className="text-sm text-muted">
              {tenant.data?.slug} · {tenant.data?.timezone}
            </div>
          </div>
        </header>

        <DataTruckSection base={base} onSaved={() => refresh("datatruck")} tenantId={tenantId} />
        <AdminsSection base={base} tenantId={tenantId} onChanged={() => refresh("admins")} />
        <MappingsSection base={base} tenantId={tenantId} onChanged={() => refresh("mappings")} />
        <FleetSection base={base} tenantId={tenantId} onChanged={() => refresh("fleet")} />
        <HrSection base={base} tenantId={tenantId} />
        <SchedulesSection base={base} tenantId={tenantId} />
        <RunNowSection base={base} />
        <RunsSection base={base} tenantId={tenantId} />
      </div>
    </div>
  );
}

function DataTruckSection({
  base,
  tenantId,
  onSaved,
}: {
  base: string;
  tenantId: number;
  onSaved: () => void;
}) {
  const status = useQuery<DataTruckStatus>({
    queryKey: ["datatruck", tenantId],
    queryFn: () => api.get<DataTruckStatus>(`${base}/datatruck`),
  });
  const [baseUrl, setBaseUrl] = useState("");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [revealed, setRevealed] = useState<string | null>(null);

  useEffect(() => {
    if (!status.data) return;
    setBaseUrl(status.data.base_url ?? "");
    setUsername(status.data.username ?? "");
  }, [status.data]);

  const save = useMutation({
    mutationFn: () =>
      api.put<DataTruckStatus>(`${base}/datatruck`, {
        base_url: baseUrl,
        username,
        password,
      }),
    onSuccess: () => {
      setPassword("");
      setRevealed(null);
      onSaved();
    },
  });

  const reveal = useMutation({
    mutationFn: () => api.post<DataTruckSecret>(`${base}/datatruck/reveal`, {}),
    onSuccess: (s) => setRevealed(s.password),
  });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    save.mutate();
  };

  return (
    <Section
      title="DataTruck login"
      hint="The credentials the bot fetches this customer's numbers with."
    >
      <form onSubmit={submit} className="space-y-4">
        <Field label="API base URL">
          <Input
            value={baseUrl}
            onChange={(e) => setBaseUrl(e.target.value)}
            placeholder="https://sam.discountapp.io/api"
            required
          />
        </Field>
        <div className="grid gap-4 md:grid-cols-2">
          <Field label="Username">
            <Input value={username} onChange={(e) => setUsername(e.target.value)} required />
          </Field>
          <Field label="Password">
            <Input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={status.data?.configured ? "unchanged" : ""}
              required={!status.data?.configured}
            />
          </Field>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <Button type="submit" disabled={save.isPending}>
            {save.isPending ? "Saving…" : "Save"}
          </Button>
          {status.data?.configured && (
            <Button
              type="button"
              variant="ghost"
              onClick={() => reveal.mutate()}
              disabled={reveal.isPending}
            >
              Reveal password
            </Button>
          )}
          {revealed !== null && (
            <code className="rounded-card bg-canvas px-3 py-1.5 text-sm">{revealed}</code>
          )}
        </div>
        {revealed !== null && (
          <div className="text-xs text-muted">This reveal was written to the audit log.</div>
        )}
        {save.error && <div className="text-sm text-danger">{(save.error as Error).message}</div>}
        {reveal.error && (
          <div className="text-sm text-danger">{(reveal.error as Error).message}</div>
        )}
      </form>
    </Section>
  );
}

function AdminsSection({
  base,
  tenantId,
  onChanged,
}: {
  base: string;
  tenantId: number;
  onChanged: () => void;
}) {
  const admins = useQuery<AdminRow[]>({
    queryKey: ["admins", tenantId],
    queryFn: () => api.get<AdminRow[]>(`${base}/admins`),
  });
  const [chatId, setChatId] = useState("");
  const [displayName, setDisplayName] = useState("");
  const qc = useQueryClient();

  const done = () => {
    setChatId("");
    setDisplayName("");
    onChanged();
    qc.invalidateQueries({ queryKey: ["chats"] });
  };

  const add = useMutation({
    mutationFn: () =>
      api.put<AdminRow>(`${base}/admins`, {
        telegram_id: chatId,
        display_name: displayName || null,
      }),
    onSuccess: done,
  });
  const remove = useMutation({
    mutationFn: (telegramId: string) => api.del(`${base}/admins/${telegramId}`),
    onSuccess: done,
  });

  return (
    <Section
      title="Report recipients"
      hint="Private chats the admin totals report is sent to. With none, the run fails rather than passing silently."
    >
      {admins.data?.length === 0 && <Empty>No recipients — scheduled runs will fail.</Empty>}
      <Rows>
        {admins.data?.map((a) => (
          <Row key={a.id}>
            <span>
              <code>{a.telegram_id}</code>
              {a.display_name && <span className="ml-2 text-muted">{a.display_name}</span>}
            </span>
            <Button variant="ghost" onClick={() => remove.mutate(a.telegram_id)}>
              Remove
            </Button>
          </Row>
        ))}
      </Rows>
      <div className="mt-4 grid gap-4 md:grid-cols-[2fr_2fr_auto] md:items-end">
        <Field label="Chat">
          <ChatPicker
            value={chatId}
            onChange={setChatId}
            tenantId={tenantId}
            placeholder="123456789"
          />
        </Field>
        <Field label="Label (optional)">
          <Input value={displayName} onChange={(e) => setDisplayName(e.target.value)} />
        </Field>
        <Button onClick={() => add.mutate()} disabled={!chatId || add.isPending}>
          Add
        </Button>
      </div>
      {add.error && <div className="mt-2 text-sm text-danger">{(add.error as Error).message}</div>}
    </Section>
  );
}

function MappingsSection({
  base,
  tenantId,
  onChanged,
}: {
  base: string;
  tenantId: number;
  onChanged: () => void;
}) {
  const mappings = useQuery<MappingRow[]>({
    queryKey: ["mappings", tenantId],
    queryFn: () => api.get<MappingRow[]>(`${base}/company-mappings`),
  });
  const [mcId, setMcId] = useState("");
  const [chatId, setChatId] = useState("");
  const qc = useQueryClient();

  const done = () => {
    setMcId("");
    setChatId("");
    onChanged();
    qc.invalidateQueries({ queryKey: ["chats"] });
  };

  const add = useMutation({
    mutationFn: () =>
      api.put<MappingRow>(`${base}/company-mappings`, {
        mc_id: Number(mcId),
        telegram_chat_id: chatId,
      }),
    onSuccess: done,
  });
  const remove = useMutation({
    mutationFn: (id: number) => api.del(`${base}/company-mappings/${id}`),
    onSuccess: done,
  });

  return (
    <Section
      title="Company groups"
      hint="One MC to one group. Leave empty and no per-company report is sent at all."
    >
      {mappings.data?.length === 0 && <Empty>No groups mapped — company reports are skipped.</Empty>}
      <Rows>
        {mappings.data?.map((m) => (
          <Row key={m.id}>
            <span>
              MC <b>{m.mc_id}</b>
              {m.mc_name_snapshot && <span className="ml-2 text-muted">{m.mc_name_snapshot}</span>}
              <code className="ml-2">{m.telegram_chat_id}</code>
            </span>
            <Button variant="ghost" onClick={() => remove.mutate(m.mc_id)}>
              Remove
            </Button>
          </Row>
        ))}
      </Rows>
      <div className="mt-4 grid gap-4 md:grid-cols-[1fr_2fr_auto] md:items-end">
        <Field label="MC id">
          <Input value={mcId} onChange={(e) => setMcId(e.target.value)} type="number" min={1} />
        </Field>
        <Field label="Group">
          <ChatPicker value={chatId} onChange={setChatId} tenantId={tenantId} />
        </Field>
        <Button onClick={() => add.mutate()} disabled={!mcId || !chatId || add.isPending}>
          Add
        </Button>
      </div>
      {add.error && <div className="mt-2 text-sm text-danger">{(add.error as Error).message}</div>}
    </Section>
  );
}

function FleetSection({
  base,
  tenantId,
  onChanged,
}: {
  base: string;
  tenantId: number;
  onChanged: () => void;
}) {
  const fleet = useQuery<FleetChatStatus>({
    queryKey: ["fleet", tenantId],
    queryFn: () => api.get<FleetChatStatus>(`${base}/fleet-chat`),
  });
  const [chatId, setChatId] = useState("");
  const qc = useQueryClient();

  useEffect(() => {
    if (fleet.data) setChatId(fleet.data.telegram_chat_id ?? "");
  }, [fleet.data]);

  const save = useMutation({
    mutationFn: () => api.put<FleetChatStatus>(`${base}/fleet-chat`, { telegram_chat_id: chatId }),
    onSuccess: () => {
      onChanged();
      qc.invalidateQueries({ queryKey: ["chats"] });
    },
  });

  return (
    <Section title="Fleet chat" hint="Where the fleet-statuses report goes.">
      <div className="grid gap-4 md:grid-cols-[3fr_auto] md:items-end">
        <Field label="Chat">
          <ChatPicker value={chatId} onChange={setChatId} tenantId={tenantId} />
        </Field>
        <Button onClick={() => save.mutate()} disabled={!chatId || save.isPending}>
          Save
        </Button>
      </div>
      {save.error && <div className="mt-2 text-sm text-danger">{(save.error as Error).message}</div>}
    </Section>
  );
}

function HrSection({ base, tenantId }: { base: string; tenantId: number }) {
  const settings = useQuery<ReportSettings>({
    queryKey: ["report-settings", tenantId],
    queryFn: () => api.get<ReportSettings>(`${base}/report-settings`),
  });
  const [form, setForm] = useState<Record<string, string>>({});
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (!settings.data) return;
    setForm(
      Object.fromEntries(
        HR_FIELDS.map(({ key }) => [key, settings.data[key]?.toString() ?? ""]),
      ),
    );
  }, [settings.data]);

  const save = useMutation({
    mutationFn: () =>
      api.put<ReportSettings>(
        `${base}/report-settings`,
        Object.fromEntries(
          HR_FIELDS.map(({ key }) => [
            key,
            (form[key] ?? "").trim() === "" ? null : Number(form[key]),
          ]),
        ),
      ),
    onSuccess: () => setSaved(true),
  });

  return (
    <Section
      title="HR targets"
      hint="Rendered as count/target in the TOTALS block. Leave every box blank for plain counts."
    >
      <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
        {HR_FIELDS.map(({ key, label }) => (
          <Field key={key} label={label}>
            <Input
              type="number"
              min={0}
              placeholder="—"
              value={form[key] ?? ""}
              onChange={(e) => {
                setSaved(false);
                setForm((f) => ({ ...f, [key]: e.target.value }));
              }}
            />
          </Field>
        ))}
      </div>
      <div className="mt-4 flex items-center gap-3">
        <Button onClick={() => save.mutate()} disabled={save.isPending}>
          {save.isPending ? "Saving…" : "Save targets"}
        </Button>
        {saved && !save.isPending && <span className="text-sm text-muted">Saved.</span>}
        {save.error && <span className="text-sm text-danger">{(save.error as Error).message}</span>}
      </div>
    </Section>
  );
}

function SchedulesSection({ base, tenantId }: { base: string; tenantId: number }) {
  const schedules = useQuery<ScheduleRow[]>({
    queryKey: ["schedules", tenantId],
    queryFn: () => api.get<ScheduleRow[]>(`${base}/schedules`),
  });
  const qc = useQueryClient();
  const [kind, setKind] = useState<string>(KINDS[0]);
  const [cron, setCron] = useState("0 9,19 * * *");
  const [tz, setTz] = useState("");

  const refresh = () => qc.invalidateQueries({ queryKey: ["schedules", tenantId] });

  const save = useMutation({
    mutationFn: () =>
      api.put<ScheduleRow>(`${base}/schedules`, {
        kind,
        cron_expression: cron,
        timezone: tz || "UTC",
        is_active: true,
      }),
    onSuccess: refresh,
  });
  const toggle = useMutation({
    mutationFn: (s: ScheduleRow) =>
      api.put<ScheduleRow>(`${base}/schedules`, {
        kind: s.kind,
        cron_expression: s.cron_expression,
        timezone: s.timezone,
        is_active: !s.is_active,
      }),
    onSuccess: refresh,
  });
  const remove = useMutation({
    mutationFn: (id: number) => api.del(`${base}/schedules/${id}`),
    onSuccess: refresh,
  });

  return (
    <Section title="Schedules" hint="Cron is evaluated in the schedule's own timezone.">
      {schedules.data?.length === 0 && <Empty>Nothing scheduled — reports only run on demand.</Empty>}
      <Rows>
        {schedules.data?.map((s) => (
          <Row key={s.id}>
            <span>
              <b>{s.kind.replace("_", " ")}</b>
              <code className="ml-2">{s.cron_expression}</code>
              <span className="ml-2 text-muted">{s.timezone}</span>
            </span>
            <span className="flex items-center gap-2">
              <Badge tone={s.is_active ? "good" : "neutral"}>
                {s.is_active ? "active" : "paused"}
              </Badge>
              <Button variant="ghost" onClick={() => toggle.mutate(s)}>
                {s.is_active ? "Pause" : "Resume"}
              </Button>
              <Button variant="ghost" onClick={() => remove.mutate(s.id)}>
                Delete
              </Button>
            </span>
          </Row>
        ))}
      </Rows>
      <div className="mt-4 grid gap-4 md:grid-cols-[2fr_2fr_2fr_auto] md:items-end">
        <Field label="Report">
          <select
            value={kind}
            onChange={(e) => setKind(e.target.value)}
            className="w-full rounded-card border border-panel/10 bg-white px-4 py-2.5 text-sm text-panel focus:border-primary focus:outline-none"
          >
            {KINDS.map((k) => (
              <option key={k} value={k}>
                {k.replace("_", " ")}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Cron">
          <Input value={cron} onChange={(e) => setCron(e.target.value)} />
        </Field>
        <Field label="Timezone">
          <Input value={tz} onChange={(e) => setTz(e.target.value)} placeholder="UTC" />
        </Field>
        <Button onClick={() => save.mutate()} disabled={save.isPending}>
          Save
        </Button>
      </div>
      {save.error && <div className="mt-2 text-sm text-danger">{(save.error as Error).message}</div>}
    </Section>
  );
}

function RunNowSection({ base }: { base: string }) {
  const [preview, setPreview] = useState<RunNowResponse | null>(null);
  const run = useMutation({
    mutationFn: (kind: string) =>
      api.post<RunNowResponse>(`${base}/reports/run-now?kind=${kind}`, {}),
    onSuccess: setPreview,
  });

  return (
    <Section
      title="Preview"
      hint="Renders against live DataTruck data. Nothing is delivered and no snapshot is written."
    >
      <div className="flex flex-wrap gap-2">
        {KINDS.map((k) => (
          <Button key={k} onClick={() => run.mutate(k)} disabled={run.isPending}>
            {k.replace("_", " ")}
          </Button>
        ))}
      </div>
      {run.error && <div className="mt-3 text-sm text-danger">{(run.error as Error).message}</div>}
      {preview && (
        <div className="mt-4 rounded-card bg-panel p-4">
          <div className="mb-2 text-xs text-white/60">
            {preview.kind} · {preview.total_chunks} chunk(s)
          </div>
          <pre className="whitespace-pre-wrap text-xs text-white/90">{preview.preview}</pre>
        </div>
      )}
    </Section>
  );
}

function RunsSection({ base, tenantId }: { base: string; tenantId: number }) {
  const runs = useQuery<RunRow[]>({
    queryKey: ["runs", tenantId],
    queryFn: () => api.get<RunRow[]>(`${base}/runs`),
  });

  return (
    <Section title="Recent runs">
      {runs.data?.length === 0 && <Empty>No runs yet.</Empty>}
      <Rows>
        {runs.data?.map((r) => (
          <Row key={r.id}>
            <span>
              <Badge tone={statusTone(r.status)}>{r.status}</Badge>
              <span className="ml-2">{r.kind.replace("_", " ")}</span>
              <span className="ml-2 text-muted">{new Date(r.started_at).toLocaleString()}</span>
              {r.error && <div className="mt-1 text-xs text-danger">{r.error}</div>}
            </span>
            <span className="text-muted">
              {r.successful_deliveries}/{r.total_deliveries} delivered
            </span>
          </Row>
        ))}
      </Rows>
    </Section>
  );
}
